import { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Loader2, MessageCircle, X } from 'lucide-react';
import axios from 'axios';
import { getApiBaseUrl } from '../../utils/config';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface CallChatInterfaceProps {
  callId: string;
  isDarkMode: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
}

export default function CallChatInterface({ callId, isDarkMode, isOpen = true, onClose, messages, setMessages }: CallChatInterfaceProps) {
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      role: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      // Prepare chat history (last 10 messages for context)
      const chatHistory = messages.slice(-10).map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const response = await axios.post(
        `${getApiBaseUrl()}/api/v1/calls/${callId}/chat`,
        {
          question: userMessage.content,
          chat_history: chatHistory
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const assistantMessage: Message = {
        role: 'assistant',
        content: response.data.answer,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error: any) {
      console.error('Error sending message:', error);
      
      const errorMessage: Message = {
        role: 'assistant',
        content: error.response?.data?.detail || 
                 'Sorry, I encountered an error processing your question. Please try again.',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Suggested questions
  const suggestedQuestions = [
    "What was the representative's strongest skill in this call?",
    "Where did the rep lose momentum?",
    "How well did they handle objections?",
    "What should they improve for next time?",
    "Can you summarize the customer's main concerns?"
  ];

  const handleSuggestedQuestion = (question: string) => {
    setInputValue(question);
    inputRef.current?.focus();
  };

  if (!isOpen) return null;

  return (
    <div className={`flex flex-col h-full ${
      isDarkMode ? 'bg-[#0a1414] border-[#1a2424]' : 'bg-white border-gray-200'
    } border rounded-lg shadow-lg`}>
      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-3 border-b ${
        isDarkMode ? 'border-[#1a2424] bg-[#050a0a]' : 'border-gray-200 bg-gray-50'
      }`}>
        <div className="flex items-center gap-2">
          <MessageCircle className={`w-5 h-5 ${isDarkMode ? 'text-[#1a9f6b]' : 'text-[#1a9f6b]'}`} />
          <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
            Call Analysis Chat
          </h3>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className={`p-1 rounded hover:bg-opacity-80 ${
              isDarkMode ? 'hover:bg-[#1a2424]' : 'hover:bg-gray-200'
            }`}
          >
            <X className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {message.role === 'assistant' && (
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                isDarkMode ? 'bg-[#1a9f6b]' : 'bg-[#1a9f6b]'
              }`}>
                <Bot className="w-5 h-5 text-white" />
              </div>
            )}
            
            <div className={`max-w-[80%] rounded-lg px-4 py-2 ${
              message.role === 'user'
                ? isDarkMode
                  ? 'bg-[#1a9f6b] text-white'
                  : 'bg-[#1a9f6b] text-white'
                : isDarkMode
                ? 'bg-[#1a2424] text-gray-100'
                : 'bg-gray-100 text-[#053333]'
            }`}>
              {message.role === 'user' ? (
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</p>
              ) : (
                <div className={`text-sm leading-relaxed prose prose-sm max-w-none ${
                  isDarkMode ? 'prose-invert' : ''
                } prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-pre:my-2`}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {message.content}
                  </ReactMarkdown>
                </div>
              )}
              <p className={`text-xs mt-1 ${
                message.role === 'user'
                  ? 'text-white text-opacity-70'
                  : isDarkMode
                  ? 'text-gray-500'
                  : 'text-gray-500'
              }`}>
                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>

            {message.role === 'user' && (
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                isDarkMode ? 'bg-[#053333]' : 'bg-[#053333]'
              }`}>
                <User className="w-5 h-5 text-white" />
              </div>
            )}
          </div>
        ))}

        {isLoading && (
          <div className="flex gap-3 justify-start">
            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
              isDarkMode ? 'bg-[#1a9f6b]' : 'bg-[#1a9f6b]'
            }`}>
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div className={`rounded-lg px-4 py-2 ${
              isDarkMode ? 'bg-[#1a2424]' : 'bg-gray-100'
            }`}>
              <Loader2 className={`w-5 h-5 animate-spin ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`} />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Suggested Questions (shown when no user messages yet) */}
      {messages.filter(m => m.role === 'user').length === 0 && (
        <div className={`px-4 py-2 border-t ${
          isDarkMode ? 'border-[#1a2424]' : 'border-gray-200'
        }`}>
          <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Suggested questions:
          </p>
          <div className="flex flex-wrap gap-2">
            {suggestedQuestions.slice(0, 3).map((question, index) => (
              <button
                key={index}
                onClick={() => handleSuggestedQuestion(question)}
                className={`text-xs px-3 py-1 rounded-full transition-colors ${
                  isDarkMode
                    ? 'bg-[#1a2424] text-gray-300 hover:bg-[#1a9f6b] hover:text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-[#1a9f6b] hover:text-white'
                }`}
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className={`p-4 border-t ${
        isDarkMode ? 'border-[#1a2424]' : 'border-gray-200'
      }`}>
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask anything about this call..."
            disabled={isLoading}
            className={`flex-1 px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#1a9f6b] ${
              isDarkMode
                ? 'bg-[#050a0a] border-[#1a2424] text-white placeholder-gray-500'
                : 'bg-white border-gray-300 text-[#053333] placeholder-gray-400'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isLoading}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              !inputValue.trim() || isLoading
                ? isDarkMode
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-[#1a9f6b] text-white hover:bg-[#148856]'
            }`}
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
